import axios from 'axios';
import apiClient from './client';
import type { MemberStats } from './families';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface InviteCreated {
  token: string;
  url: string;
  expires_at: string;
}

export interface InvitePreview {
  invite_kind: string;
  resource_name: string;
  family_id?: number | null;
  member_id?: number | null;
  inviter_display: string | null;
  expires_at: string;
  status: string;
}

export interface SharedMemberItem {
  member_id: number;
  name: string;
  family_id: number;
  family_name: string;
  /** Same derived counters as `FamilyMember.stats`; populated by the
   * `/api/sharing/members` endpoint. Optional for backwards
   * compatibility with older API versions. */
  stats?: MemberStats | null;
}

export interface GranteeItem {
  user_id: number;
  email: string | null;
  full_name: string | null;
  created_at: string | null;
}

export interface PendingInviteRow {
  id: number;
  invite_kind: string;
  token_hint: string | null;
  expires_at: string | null;
  status: string;
  family_id: number | null;
  member_id: number | null;
}

export async function fetchInvitePreview(token: string): Promise<InvitePreview> {
  const { data } = await axios.get<InvitePreview>(
    `${API_URL}/api/sharing/invites/preview/${encodeURIComponent(token)}`
  );
  return data;
}

export const sharingApi = {
  createFamilyInvite: async (familyId: number, expiresInDays = 7): Promise<InviteCreated> => {
    const { data } = await apiClient.post<InviteCreated>('/api/sharing/family-invites', {
      family_id: familyId,
      expires_in_days: expiresInDays,
    });
    return data;
  },

  createMemberInvite: async (memberId: number, expiresInDays = 7): Promise<InviteCreated> => {
    const { data } = await apiClient.post<InviteCreated>('/api/sharing/member-invites', {
      member_id: memberId,
      expires_in_days: expiresInDays,
    });
    return data;
  },

  acceptInvite: async (token: string): Promise<void> => {
    await apiClient.post(`/api/sharing/invites/${encodeURIComponent(token)}/accept`);
  },

  listInboundMembers: async (): Promise<SharedMemberItem[]> => {
    const { data } = await apiClient.get<SharedMemberItem[]>('/api/sharing/members');
    return data;
  },

  listFamilyGrantees: async (familyId: number): Promise<GranteeItem[]> => {
    const { data } = await apiClient.get<GranteeItem[]>(`/api/sharing/families/${familyId}/grantees`);
    return data;
  },

  removeFamilyGrantee: async (familyId: number, userId: number): Promise<void> => {
    await apiClient.delete(`/api/sharing/families/${familyId}/access/${userId}`);
  },

  listMemberGrantees: async (memberId: number): Promise<GranteeItem[]> => {
    const { data } = await apiClient.get<GranteeItem[]>(`/api/sharing/members/${memberId}/grantees`);
    return data;
  },

  removeMemberGrantee: async (memberId: number, userId: number): Promise<void> => {
    await apiClient.delete(`/api/sharing/members/${memberId}/access/${userId}`);
  },

  listFamilyPendingInvites: async (familyId: number): Promise<PendingInviteRow[]> => {
    const { data } = await apiClient.get<PendingInviteRow[]>(
      `/api/sharing/families/${familyId}/pending-invites`
    );
    return data;
  },

  listMemberPendingInvites: async (memberId: number): Promise<PendingInviteRow[]> => {
    const { data } = await apiClient.get<PendingInviteRow[]>(
      `/api/sharing/members/${memberId}/pending-invites`
    );
    return data;
  },

  revokeInviteRow: async (inviteId: number): Promise<void> => {
    await apiClient.post(`/api/sharing/invite-rows/${inviteId}/revoke`);
  },
};
